/** 工种决定卡片内部底色：同一工种跨项目、阶段及设备保持同色。
 * 日历使用更饱和的工种底色，详情/编辑仅在标题栏使用同色，正文保持白底。
 * 边框统一细线，紧急程度仍由独立标签表达，不改变已有工种和阶段的对应关系。
 * 分类键来自已有施工工种目录；阶段只作文字说明。所有颜色同时配名称，避免仅凭颜色辨认。
 */
import React from "react";
import { PHASES } from "../../data/constants";

// 原先大量浅粉彩在月历小格中难以区分；提高饱和度、适当降低亮度，保留固定色相。
// 颜色配深色文字；不能直接把深色圆点色铺满背景，否则窄格文字会失去对比度。
// 灰底仅表示尚未指定工种，不推断分包商职业；紧急程度由独立文字标签表达。
export const SCHEDULE_TRADES = {
  demolition: { color: "#64748b", background: "#94a3b8", zh: "拆除", en: "Demolition" },
  foundation: { color: "#8b5cf6", background: "#b388ff", zh: "地基", en: "Foundation" },
  // 参考施工排程图用橙色识别木结构；与蓝色给排水、电工形成更清楚的暖冷对比。
  framing: { color: "#ea8a00", background: "#ffb52e", zh: "木结构", en: "Framing" },
  plumbing: { color: "#0ea5e9", background: "#4db5ff", zh: "给排水", en: "Plumbing" },
  electrical: { color: "#06b6d4", background: "#22d3ee", zh: "电工", en: "Electrical" },
  hvac: { color: "#14b8a6", background: "#2dd4bf", zh: "空调", en: "HVAC" },
  roof: { color: "#ec4899", background: "#f472b6", zh: "屋顶", en: "Roof" },
  exterior_wall: { color: "#c084fc", background: "#d68cff", zh: "外墙", en: "Exterior wall" },
  insulation: { color: "#10b981", background: "#5dd977", zh: "保温隔热", en: "Insulation" },
  drywall: { color: "#84cc16", background: "#b5df36", zh: "石膏板", en: "Drywall" },
  wood_floor: { color: "#f59e0b", background: "#ffc247", zh: "木地板", en: "Wood flooring" },
  cabinet_material: { color: "#b400e6", background: "#ed79f4", zh: "橱柜材料", en: "Cabinet material" },
  cabinet_install: { color: "#00b3a4", background: "#21c89b", zh: "橱柜安装", en: "Cabinet installation" },
  renovation: { color: "#ff2d55", background: "#ff9473", zh: "装修", en: "Renovation" },
  interior_material: { color: "#4338ff", background: "#aa92fa", zh: "装饰材料", en: "Decor material" },
};
const UNKNOWN = { color: "#9ca3af", background: "#f1f5f9", zh: "未指定工种", en: "Unspecified trade" };
const canonicalKey = (key) => ["wood_floor_material", "wood_floor_install"].includes(key) ? "wood_floor" : key;

/** 只由稳定工种键选色，不受排序、阶段或紧急程度影响；自定义工种也可立即使用。 */
export function scheduleTrade(task = {}) {
  const key = canonicalKey(task.tradeKey);
  if (!key) return { ...UNKNOWN, key: "" };
  if (Object.prototype.hasOwnProperty.call(SCHEDULE_TRADES, key)) return { ...SCHEDULE_TRADES[key], key };
  // 自定义工种同样采用鲜明配色；仍按键确定色相，不随页面排序漂移。
  let hash = 0;
  for (const char of key) hash = (Math.imul(hash, 31) + char.charCodeAt(0)) | 0;
  const hue = (hash >>> 0) % 360;
  return { key, color: `hsl(${hue}, 95%, 43%)`, background: `hsl(${hue}, 90%, 76%)`, zh: task.tradeName || key, en: task.tradeName || key };
}

/** 月历、周历、当天卡片及工人摘要使用工种底色；详情/编辑正文不能套用此样式。 */
export const scheduleTaskStyle = (task) => ({ borderColor: "#d4dbe4", borderWidth: 1, backgroundColor: scheduleTrade(task).background });
export const scheduleTradeLabel = (task, zh) => { const trade = scheduleTrade(task); return zh == null ? `${trade.zh} / ${trade.en}` : trade[zh ? "zh" : "en"]; };
const phaseLabel = (task, zh) => {
  const phase = PHASES.find((p) => p.key === task.phaseKey);
  return phase ? (zh == null ? `${phase.zh} / ${phase.en}` : phase[zh ? "zh" : "en"]) : "";
};

/** 紧凑模式用于七列月历；完整模式给详情、当天卡片及工人页显示工种和独立阶段标签。 */
export default function ScheduleTrade({ task, zh, compact = false }) {
  const trade = scheduleTrade(task), label = scheduleTradeLabel(task, zh), phase = phaseLabel(task, zh);
  // 手机七列格子优先露出工种名，完整阶段在当天卡片显示，避免“电工”也被阶段文字挤成省略号。
  if (compact) return <span className="block truncate font-medium" title={[label, phase].filter(Boolean).join(" · ")}>{label}{phase && <span className="hidden sm:inline"> · {phase}</span>}</span>;
  return <span className="flex flex-wrap items-center gap-1.5" data-testid="schedule-trade-label">
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-white/70 px-2 py-1 text-xs text-slate-800" style={{ backgroundColor: trade.background }}><span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: trade.color }} /><span className="break-words min-w-0">{label}</span></span>
    {phase && <span className="rounded-md bg-white/70 px-2 py-1 text-xs text-slate-600">{phase}</span>}
  </span>;
}
